import './App.css';
import { useEffect, useRef } from 'react';
import { isPast, isFuture, closestIndexTo, isWithinInterval, sub } from "date-fns";

/**
 * Renders a salary schedule component based on provided props.
 * 
 * This component calculates and displays a salary progression schedule,
 * including step increases, anniversaries, and longevity bonuses if applicable.
 * 
 * @param {Object} props - The properties passed to the component.
 * @param {string} props.appointedDate - The initial appointment date.
 * @param {string} props.equateDate - The equate date for salary calculations.
 * @param {number} props.termLength - The length of the term in milliseconds.
 * @param {string} props.stepCode - The initial step code (e.g., "1A").
 * @param {number} props.terms - The number of terms to calculate.
 * @param {boolean} props.includeLongevity - Whether to include longevity bonuses.
 * @param {string} [props.title] - Optional title for the salary schedule.
 * @param {string} [props.content] - Optional content to display above the schedule.
 * @param {string} [props.handleSinglYearDifferential] - Handles single year differentials for the final step.
 * 
 * @returns {JSX.Element} A React component displaying the salary schedule.
 */
function SalarySchedule(props) {
    // Date Variables
    let oneYear = 31556926000;
    const formateDate = { year: 'numeric', month: 'short', day: 'numeric' };
    const appointedDate = new Date(props.appointedDate);
    let combinedEvents = [];
    let appointedDateYear = appointedDate.getFullYear();
    let equateDate = new Date(props.equateDate);
    let currentDate = new Date();
    let maxTerms = 15;
    if(props.jobTitle == "school-psychologist" || props.jobTitle == "social-worker"){
        maxTerms = 13;
    }
    if(props.jobTitle == "lab-tech"){
        maxTerms = 10;
    }
    if(props.jobTitle == 'staff-nurse' || props.jobTitle == 'head-nurse' || props.jobTitle == 'supervisor-nurse'){
        maxTerms = 19;
    }
    if(props.jobTitle === 'occupational-therapist' || props.jobTitle === 'physical-therapist' || props.jobTitle === 'audiologist'){
        maxTerms = 11;
    }
    const stepIncreaseDate = new Date();
    stepIncreaseDate.setMonth(2);
    stepIncreaseDate.setDate(1);
    stepIncreaseDate.setFullYear(appointedDate.getFullYear());
    stepIncreaseDate.setHours(0);
    stepIncreaseDate.setMinutes(0);
    stepIncreaseDate.setSeconds(0);
    stepIncreaseDate.setMilliseconds(0);
    let totalTermLength = props.termLength;
    let singleYearAnniversary = appointedDate;
    let singleYearAnniversaryArray = [];

    if(isNaN(equateDate)){
      if(totalTermLength){
        if(props.jobTitle === 'staff-nurse' || props.jobTitle === 'head-nurse' || props.jobTitle === 'supervisor-nurse' || props.jobTitle === 'occupational-therapist' || props.jobTitle === 'physical-therapist' || props.jobTitle === 'audiologist'){
            equateDate = sub(appointedDate, { years: (totalTermLength) });
        }
        else{
            equateDate = sub(appointedDate, { years: (totalTermLength / 2) });
        }
      }
      else{
        equateDate = new Date(appointedDate);
      }
    }
    
    equateDate.setFullYear(appointedDate.getFullYear());
    let stepProgression = [];
    let futureDates = 0;
    let targetIndex = 0;
    const currentStepRef = useRef(null);
    let prevStep = parseInt(props.stepCode.substring(0, 1)) + 1;
    let lastFoundLongevityIndex = 0;
    let error = false;

    // Step Code Array
    let stepCodes = [
        ["A", "B"],
        ["V", "W"],
        ["C", "D"],
        ["G", "H"],
        ["E", "F"],
        ["I", "J"],
        ["K", "L"],
        ["M", "N"]
    ];

    const excludedNursingSteps = [8, 9, 11, 12, 14, 16, 19];
    if(props.jobTitle === 'staff-nurse' || props.jobTitle === 'head-nurse' || props.jobTitle === 'supervisor-nurse'){
        stepCodes = [
            ["A", "B"],
            ["A", "B"],
            ["A", "B"],
            ["A", "B"],
            ["A", "B"],
            ["A", "B"],
            ["A", "B"],
            ["A", "B"],
            ["A", "B"],
            ["A", "B"],
            ["A", "B"],
            ["A", "B"],
            ["A", "B"],
            ["A", "B"],
            ["A", "B"],
            ["A", "B"],
            ["A", "B"],
            ["A", "B"],
            ["A", "B"],
            ["A", "B"],
        ];
    }

    const excludedTherapistSteps = [1, 3, 4, 6, 8, 9, 10, 11];
    if(props.jobTitle === 'occupational-therapist' || props.jobTitle === 'physical-therapist' || props.jobTitle === 'audiologist'){
        stepCodes = [
            ["A", "B"],
            ["A", "B"],
            ["A", "B"],
            ["A", "B"],
            ["A", "B"],
            ["A", "B"],
            ["A", "B"],
            ["A", "B"],
            ["A", "B"],
            ["A", "B"],
        ];
    }

    // Extract step letter and number from step code
    let stepLetter = props.stepCode.slice(-1);
    let stepNumber = props.stepCode.slice(0, -1);
    let noExpDiff = '';
    if(props.jobTitle === 'staff-nurse' || props.jobTitle === 'head-nurse' || props.jobTitle === 'supervisor-nurse'){
        stepNumber = stepNumber - 1;
        if(stepNumber === 0){
            noExpDiff = 'No'
        }
    }
    else if(props.jobTitle === 'occupational-therapist' || props.jobTitle === 'physical-therapist' || props.jobTitle === 'audiologist'){
        stepNumber = stepNumber - 1;
        if(stepNumber < 2){
            noExpDiff = 'No'
        }
    }

    // Determine starting step number
    let startingStepNumber = stepNumber;
    let startingYearStepNumber = stepNumber;
    if(props.jobTitle === 'staff-nurse' || props.jobTitle === 'head-nurse' || props.jobTitle === 'supervisor-nurse'){
        if(totalTermLength === "20"){
            startingStepNumber = 20;
        }   
        else{
            while(excludedNursingSteps.includes(parseInt(startingStepNumber))){
                startingStepNumber--;
            }
        }
    }
    if(props.jobTitle === 'occupational-therapist' || props.jobTitle === 'physical-therapist' || props.jobTitle === 'audiologist'){
        if(totalTermLength === "12"){
            startingStepNumber = 12;
        }   
        else{
            while(excludedTherapistSteps.includes(parseInt(startingStepNumber))){
                startingStepNumber--;
            }
        }

        if (startingStepNumber > 0){
            if(startingStepNumber === 2){
                startingStepNumber = "Senior Therapist"
            }
            else{
                startingStepNumber = startingStepNumber - 2;
            }
        }
    }

    let stepCodeIndex;
    for(var i = 0; i < stepCodes.length; i++) {
        if(stepCodes[i].includes(stepLetter)) {
            stepCodeIndex = stepCodes[i];
        }
    }

    // Create array of dates
    const datesArray =  [
        appointedDate,
        equateDate,
        stepIncreaseDate
    ];
    
    //Sort dates into ascending order
    datesArray.sort((a, b) => a - b);
    let anniversaryOne;
    for(let i = 0; i < datesArray.length; i++){
        if(datesArray[i] === appointedDate){
            if((i + 1) >= datesArray.length){
                anniversaryOne = datesArray[0];
            }
            else{
                anniversaryOne = datesArray[i + 1];
            }
        }
    }

    let anniversaryTwo = anniversaryOne === equateDate ? stepIncreaseDate : equateDate;

    // Switch anniversaryOne and anniversaryTwo if anniversaryOne is the same as appointed date
    if(anniversaryOne.getTime() === appointedDate.getTime() || anniversaryOne.getTime() === appointedDate.getTime()){
        let tempDate = anniversaryOne;
        anniversaryOne = anniversaryTwo;
        anniversaryTwo = tempDate;
    }

    let longevityCounter = 0;
    if(props.jobTitle != 'staff-nurse' && props.jobTitle != 'head-nurse' && props.jobTitle != 'supervisor-nurse' && props.jobTitle != 'occupational-therapist' && props.jobTitle != 'physical-therapist' && props.jobTitle != 'audiologist'){
        for(let i = (props.terms + 1); i <= maxTerms; i++) {
            if(i % 2 === 0) {
                longevityCounter ++;
                stepNumber++;
                if(props.includeLongevity){
                    stepProgression.push(stepNumber + stepCodeIndex[0])
                }
                else{
                    if(longevityCounter >= 5){
                        stepProgression.push(stepNumber + "V")
                    }
                    else{
                        stepProgression.push(stepNumber + stepCodeIndex[0])
                    }
                }
            }
            else{
                if(props.includeLongevity){
                    stepProgression.push(stepNumber + stepCodeIndex[1])
                }
                else{
                    if(longevityCounter >= 5){
                        stepProgression.push(stepNumber + "W")
                    }
                    else{
                        stepProgression.push(stepNumber + stepCodeIndex[1])
                    }
                }
            }
        }
    }
    else{
        if(props.includeLongevity){
            let conceptualStep = stepNumber;
            for(let i = (stepNumber); i < maxTerms + 1; i++) {
                conceptualStep++;
                if(props.jobTitle === 'occupational-therapist' || props.jobTitle === 'physical-therapist' || props.jobTitle === 'audiologist'){
                    if (!excludedTherapistSteps.includes(conceptualStep)) {
                        stepNumber = conceptualStep;
                        stepProgression.push(stepNumber + "A")
                    }
                }
                if(props.jobTitle === 'staff-nurse' || props.jobTitle === 'head-nurse' || props.jobTitle === 'supervisor-nurse'){
                    if (!excludedNursingSteps.includes(conceptualStep)) {
                        stepNumber = conceptualStep;
                        stepProgression.push(stepNumber + "A")
                    }
                }
            }
        }
        else{
            for(let i = (stepNumber); i <= maxTerms; i++) {
                stepNumber++;
                if(props.jobTitle === 'occupational-therapist' || props.jobTitle === 'physical-therapist' || props.jobTitle === 'audiologist'){
                    if (!excludedTherapistSteps.includes(stepNumber)) {
                        stepProgression.push(stepNumber + "A")
                    }
                }
                if(props.jobTitle === 'staff-nurse' || props.jobTitle === 'head-nurse' || props.jobTitle === 'supervisor-nurse'){
                    if (!excludedNursingSteps.includes(stepNumber)) {
                        stepProgression.push(stepNumber + "A")
                    }
                }
            }
        }
    }
    
    let longevityTable = [5, 10, 13, 15, 18, 20, 22];
    if(props.jobTitle == "paraprofessionals"){
        longevityTable = [5, 15];
    }
    if(props.jobTitle == 'staff-nurse' || props.jobTitle == 'head-nurse' || props.jobTitle == 'supervisor-nurse'){
        longevityTable = [5, 7, 10, 15, 20, 22];
    }
    if(props.jobTitle === 'occupational-therapist' || props.jobTitle === 'physical-therapist' || props.jobTitle === 'audiologist'){
        longevityTable = [1, 2, 5, 7, 10, 15, 20, 22];
    }

    let longevityDateTable = [];

    if(props.includeLongevity){
        if(props.jobTitle === 'occupational-therapist' || props.jobTitle === 'physical-therapist' || props.jobTitle === 'audiologist'){
            const oneYearAfterAppointment = new Date(appointedDate);
            oneYearAfterAppointment.setFullYear(appointedDate.getFullYear() + 1);

            const anniversaryYear = oneYearAfterAppointment.getFullYear();
            let nextJan1st = new Date(anniversaryYear, 0, 1);
            if (oneYearAfterAppointment >= nextJan1st) {
                nextJan1st.setFullYear(anniversaryYear + 1);
            }
            let nextJuly1st = new Date(anniversaryYear, 6, 1);
            if (oneYearAfterAppointment >= nextJuly1st) {
                nextJuly1st.setFullYear(anniversaryYear + 1);
            }
            const firstLongevityDate = nextJan1st < nextJuly1st ? nextJan1st : nextJuly1st;
            longevityDateTable.push(firstLongevityDate);

            const secondLongevityDate = new Date(firstLongevityDate);
            secondLongevityDate.setFullYear(secondLongevityDate.getFullYear() + 1);
            longevityDateTable.push(secondLongevityDate);

            for(let i = 2; i < longevityTable.length; i++){
                longevityDateTable.push(new Date(new Date(appointedDate).setFullYear(appointedDate.getFullYear() + longevityTable[i])));
            }
        } else {
            for(i = 0; i < longevityTable.length; i++){
                const longevity = new Date(props.appointedDate);
                let currentLongevity = longevity.setFullYear(longevity.getFullYear() + longevityTable[i]);

                longevityDateTable.push(new Date(currentLongevity));
            }
        }
    }

    // Map through step status object and set corresponding step status
    if(props.currentStep == 4){
        let stepStatus = Object.entries(props.stepStatus).map(([key, value]) => {
            if(key == 'step-2-status' || key == 'step-3-status'){
                if(value == 'fail'){
                    error = true;
                }
            }
        })
    }

    // Calculate targetIndex outside of the map function
    useEffect(() => {
        const tableData = currentStepRef.current.querySelectorAll("tr td:first-of-type");
        const expDiffData = currentStepRef.current.querySelectorAll("tr td:last-of-type");
        const tableRow = currentStepRef.current.querySelectorAll("tr");
        const tableDataDatesArray = [];
        for(let i = 0; i < tableData.length; i++){
            tableDataDatesArray.push(new Date(tableData[i].textContent));
            if(isPast(new Date(tableData[i].textContent))){
                tableRow[i].classList.add('completed');
            }
            
            if(props.jobTitle != 'staff-nurse' && props.jobTitle != 'head-nurse' && props.jobTitle != 'supervisor-nurse' && props.jobTitle != 'occupational-therapist' && props.jobTitle != 'physical-therapist' && props.jobTitle != 'audiologist'){
                if(equateDate.getDate() === stepIncreaseDate.getDate() && i % 2 != 0){
                    if(equateDate.getMonth() === stepIncreaseDate.getMonth() && i % 2 != 0){
                        tableRow[i].classList.add('hidden');
                    }
                }
            }
        }
        if(props.jobTitle === 'staff-nurse' || props.jobTitle === 'head-nurse' || props.jobTitle === 'supervisor-nurse' || props.jobTitle === 'occupational-therapist' || props.jobTitle === 'physical-therapist' || props.jobTitle === 'audiologist'){
            const textContentMap = {};
            for (let i = 0; i < Math.min(expDiffData.length, 5); i++) {
                const text = expDiffData[i].textContent;
                const match = text.match(/\d+/);
                const key = match ? match[0] : text; // Use the number as the key, otherwise use the full text

                if (!textContentMap[key]) {
                    textContentMap[key] = [];
                }
                textContentMap[key].push(expDiffData[i]);
            }

            for (const key in textContentMap) {
                const elements = textContentMap[key];
                if (elements.length > 1) {
                    const parentRows = elements.map(el => el.parentElement);
                    let currentFound = false;

                    // Check for 'current' class and move it to the first element's parent row
                    for (let i = 0; i < parentRows.length; i++) {
                        if (parentRows[i].classList.contains('current')) {
                            currentFound = true;
                            if (i !== 0) {
                                parentRows[i].classList.remove('current');
                            }
                            break; // Found it, no need to check further
                        }
                    }

                    if (currentFound) {
                        parentRows[0].classList.add('current');
                    }
                    if(props.jobTitle != 'staff-nurse' && props.jobTitle != 'head-nurse' && props.jobTitle != 'supervisor-nurse' && props.jobTitle != 'occupational-therapist' && props.jobTitle != 'physical-therapist' && props.jobTitle != 'audiologist'){
                        // Hide all duplicate rows except the first one
                        for (let i = 1; i < parentRows.length; i++) {
                            parentRows[i].classList.add('hidden');
                        }
                    }
                }
            }
        }
        const currentDateResults = closestIndexTo(currentDate, tableDataDatesArray)
        if(isFuture(new Date(tableDataDatesArray[currentDateResults]))){
            if (currentDateResults > 0 && tableRow[currentDateResults - 1]) {
                tableRow[currentDateResults - 1].classList.add('current');
                const lastTd = tableRow[currentDateResults - 1].querySelector('td:last-of-type');
                if (lastTd && lastTd.textContent.includes('Experience Differential')) {
                    props.handleSinglYearDifferential(lastTd.textContent.replace(/.*Experience Differential\s*/, ''));
                } else {
                    for (let i = currentDateResults - 2; i >= 0; i--) {
                        const prevLastTd = tableRow[i].querySelector('td:last-of-type');
                        if (prevLastTd && prevLastTd.textContent.includes('Experience Differential')) {
                            props.handleSinglYearDifferential(prevLastTd.textContent.replace(/.*Experience Differential\s*/, ''));
                            break;
                        }
                    }
                }
            }
        }
        else{
            if (tableRow[currentDateResults]) {
                tableRow[currentDateResults].classList.add('current');
                const lastTd = tableRow[currentDateResults].querySelector('td:last-of-type');
                if (lastTd && lastTd.textContent.includes('Experience Differential')) {
                    props.handleSinglYearDifferential(lastTd.textContent.replace(/.*Experience Differential\s*/, ''));
                } else {
                    for (let i = currentDateResults - 1; i >= 0; i--) {
                        const prevLastTd = tableRow[i].querySelector('td:last-of-type');
                        if (prevLastTd && prevLastTd.textContent.includes('Experience Differential')) {
                            props.handleSinglYearDifferential(prevLastTd.textContent.replace(/.*Experience Differential\s*/, ''));
                            break;
                        }
                    }
                }
            }
        }

        // If on the final review step and the last row is the current one, add a message.
        if (props.currentStep === 4 && tableRow.length > 0) {
            const lastRow = tableRow[tableRow.length - 1];
            if (lastRow.classList.contains('current')) {
                const table = currentStepRef.current;
                const newRow = table.insertRow(-1); // Insert a row at the end of the table
                const newCell = newRow.insertCell(0);
                newCell.colSpan = 3; // Make the cell span all columns
                newCell.innerHTML = "You have reached the top of the salary schedule.";
                newRow.classList.add('max-salary'); // for styling purposes
            }
        }

        // Update display of current date in table to always show the first of the month
        if(props.jobTitle != 'staff-nurse' && props.jobTitle != 'head-nurse' && props.jobTitle != 'supervisor-nurse' && props.jobTitle != 'occupational-therapist' && props.jobTitle != 'physical-therapist' && props.jobTitle != 'audiologist'){
            for(let i = 0; i < tableDataDatesArray.length; i++){
                var firstOfMonth = new Date(tableDataDatesArray[i].setDate(1));
                tableDataDatesArray[i].innerHtml = firstOfMonth.toLocaleDateString("en-US", formateDate);
            }
        }

        for(let i = 0; i < tableData.length; i++){
            if(i != 0){
                switch(tableData[i].textContent){
                    case 'You have reached the top of the salary schedule.':
                        break;
                    default:
                        tableData[i].textContent = tableDataDatesArray[i].toLocaleDateString("en-US", formateDate);
                        break;
                }
            }
        }

    }, [targetIndex]);
  return (
    <>
    <div className={props.includeLongevity && 'content-box salary-schedule'}>
        {props.title && <h3 className='title'>{props.title}</h3>}
        {props.content && <p className='content' dangerouslySetInnerHTML={{__html: props.content}}></p>}
        {error == true && <div className="alert-wrapper"><div className='alert warning'><p className='content'>Possible Error</p></div></div>}
        <div className="salary-schedule-container">
        <table className='salary-schedule' ref={currentStepRef}>
        {/* Step Code and Appointed Date*/}
        <tr data-index="0">
            <td>{appointedDate.toLocaleDateString("en-US", formateDate)}</td>
            <td></td>
            <td>
                {props.jobTitle === 'staff-nurse' || props.jobTitle === 'head-nurse' || props.jobTitle === 'supervisor-nurse' || props.jobTitle === 'occupational-therapist' || props.jobTitle === 'physical-therapist' || props.jobTitle === 'audiologist' ? (
                    startingStepNumber === 'Senior Therapist' ? <>{startingStepNumber}</> : <>{noExpDiff} Experience Differential {startingStepNumber > 0 ? startingStepNumber : ''}</>
                ) : <>Step {props.stepCode}</>}
            </td>
        </tr>
        {/* Loop = Step Code + 1 term || Next Anniversary Date (march date or equate date) */}
            {stepProgression.map((step, index) => {
                let longevityRow = [];
                let longevityRows = [];
                let hasSalaryIncrease = false;
                let stepWithLongevity = step;
                if(props.jobTitle != 'staff-nurse' && props.jobTitle != 'head-nurse' && props.jobTitle != 'supervisor-nurse' && props.jobTitle != 'occupational-therapist' && props.jobTitle != 'physical-therapist' && props.jobTitle != 'audiologist'){
                    if(anniversaryOne < appointedDate){
                        appointedDateYear++;
                    }
                    if (index % 2 === 0 && index !== 0) {
                        appointedDateYear++;
                    } 
                }
                else{
                    let appointedDataDiffernce;
                    const closestStep = stepProgression.reduce((prev, curr) => {
                        const prevNum = parseInt(prev.slice(0, -1));
                        const currNum = parseInt(curr.slice(0, -1));
                        return (Math.abs(currNum - startingYearStepNumber) < Math.abs(prevNum - startingYearStepNumber) ? curr : prev);
                    });

                    if (step === closestStep) {
                        const closestStepNum = parseInt(closestStep.slice(0, -1));
                        const difference = closestStepNum - startingYearStepNumber;
                        appointedDataDiffernce = difference;
                    }
                    
                    if(appointedDataDiffernce){
                        appointedDateYear += appointedDataDiffernce;
                    }
                    else{
                        if(props.jobTitle === 'occupational-therapist' || props.jobTitle === 'physical-therapist' || props.jobTitle === 'audiologist'){
                            switch(step){
                                case '5A':
                                    appointedDateYear += 3;
                                    break;
                                case '7A':
                                    appointedDateYear += 2;
                                    break;
                                case '12A':
                                    appointedDateYear += 5;
                                    break;
                                default:
                                    appointedDateYear++;
                                    break;
                            }
                        }
                        if(props.jobTitle === 'staff-nurse' || props.jobTitle === 'head-nurse' || props.jobTitle === 'supervisor-nurse'){
                            switch(step){
                                case '10A':
                                    appointedDateYear += 3;
                                    break;
                                case '13A':
                                    appointedDateYear += 3;
                                    break;
                                case '15A':
                                    appointedDateYear += 2;
                                    break;
                                case '17A':
                                    appointedDateYear += 2;
                                    break;
                                case '20A':
                                    appointedDateYear += 2;
                                    break;
                                default:
                                    appointedDateYear++;
                                    break;
                            }
                        }
                    }
                }
                // Set single year anniversary
                singleYearAnniversary.setFullYear(appointedDateYear);
                
                // Set the year for anniversaryOne
                anniversaryOne.setFullYear(appointedDateYear);
            
                // Set the year for anniversaryTwo
                anniversaryTwo.setFullYear(appointedDateYear);
            
                // Ensure anniversaryTwo is always later in the year than anniversaryOne
                if (anniversaryTwo < anniversaryOne) {
                    anniversaryTwo.setFullYear(appointedDateYear + 1);
                }

                if(currentDate < anniversaryOne || currentDate < anniversaryTwo){
                    futureDates++;
                }
                if(index === (stepProgression.length - 1)){
                    targetIndex = stepProgression.length - futureDates - 1;
                }

                if(step.substring(0, 1) >= 6){
                    hasSalaryIncrease = true
                }
                else{
                    if(step.substring(0, 1) > 1){
                        if(props.stepCode.substring(0, 1) == step.substring(0, 1)){
                            hasSalaryIncrease = false
                        }
                        else{
                            if(prevStep == parseInt(step.substring(0, 1))){
                                hasSalaryIncrease = true;
                            }
                            else{
                                hasSalaryIncrease = false;
                            }
                        }
                    }
                    else{
                        hasSalaryIncrease = false
                    }
                }
                prevStep = parseInt(step.substring(0, 1)) + 1;

                if(props.jobTitle === 'staff-nurse' || props.jobTitle === 'head-nurse' || props.jobTitle === 'supervisor-nurse' || props.jobTitle === 'occupational-therapist' || props.jobTitle === 'physical-therapist' || props.jobTitle === 'audiologist'){
                    hasSalaryIncrease = true;
                    if(props.jobTitle === 'occupational-therapist' || props.jobTitle === 'physical-therapist' || props.jobTitle === 'audiologist'){
                        stepWithLongevity = parseInt(step.slice(0, -1)) - 2;
                        if(stepWithLongevity === 0){
                            stepWithLongevity = "Senior Therapist";
                        }
                    }
                    else{
                        stepWithLongevity = step.slice(0, -1);
                    }
                }

                if(props.includeLongevity){
                    const newSingleYearAnniversary = new Date(singleYearAnniversary);
                    newSingleYearAnniversary.setFullYear(appointedDateYear);
                    singleYearAnniversaryArray.push(newSingleYearAnniversary);
                    let longevityLengthText = (longevityYear) => {
                        switch (longevityYear){
                            case 5:
                                return '(5 but less than 7)';
                            case 7:
                                return '(7 but less than 10)';
                            case 10:
                                return '(10 but less than 15)';
                            case 15:
                                return '(15 but less than 20)';
                            case 20:
                                return '(20 but less than 22)';
                            case 22:
                                return '(22 years or more)';
                            default:
                                return '';
                        }
                    };
                    if(props.jobTitle === 'occupational-therapist' || props.jobTitle === 'physical-therapist' || props.jobTitle === 'audiologist'){
                    longevityLengthText = (longevityYear) => {
                            switch (longevityYear){
                                case 1:
                                    return '(A/B first year)';
                                case 2:
                                    return '(A/B)';
                                case 5:
                                    return '(A/B + 5 but less than 7)';
                                case 7:
                                    return '(A/B + 7 but less than 10)';
                                case 10:
                                    return '(A/B + 10 but less than 15)';
                                case 15:
                                    return '(A/B + 15 but less than 20)';
                                case 20:
                                    return '(A/B + 20 but less than 22)';
                                case 22:
                                    return '(A/B + 22 years or more)';
                                default:
                                    return '';
                            }
                        };
                    }

                    // Compare current anniversary date to all dates in longevityDateTable
                    let found = '';
                    if(props.jobTitle === 'staff-nurse' || props.jobTitle === 'head-nurse' || props.jobTitle === 'supervisor-nurse' || props.jobTitle === 'occupational-therapist' || props.jobTitle === 'physical-therapist' || props.jobTitle === 'audiologist'){
                        if (index === stepProgression.length - 1) { // Only run this once

                            const startingStepNumber = parseInt(props.stepCode.slice(0, -1));
                            const experienceEvents = singleYearAnniversaryArray
                            .map((eventDate, i) => {
                                let currentStep = stepProgression[i] || step;
                                let expDiffText = "Experience Differential ";
                                if(props.jobTitle === 'occupational-therapist' || props.jobTitle === 'physical-therapist' || props.jobTitle === 'audiologist'){
                                    currentStep = parseInt(currentStep.slice(0, -1)) - 2;
                                    if(currentStep === 0){
                                        currentStep = "Senior Therapist";
                                        expDiffText = '';
                                    }
                                }
                                else{
                                    currentStep = currentStep.slice(0, -1);
                                }
                                if (startingStepNumber !== (maxTerms + 1)) {
                                    return { date: eventDate, description: `${expDiffText}${currentStep}`, type: 'experience' };
                                }
                                return null;
                            })
                            .filter(event => event !== null);

                            const longevityEvents = longevityDateTable.map((eventDate, i) => {
                                return { date: eventDate, description: `Longevity Differential ${longevityLengthText(longevityTable[i])}`, type: 'longevity' };
                            });

                            const allEvents = [...experienceEvents, ...longevityEvents];
                            const eventsByDate = new Map();

                            allEvents.forEach(event => {
                                const dateString = event.date.toISOString().split('T')[0];
                                if (eventsByDate.has(dateString)) {
                                    const existingEvent = eventsByDate.get(dateString);
                                    existingEvent.description += ` + ${event.description}`;
                                } else {
                                    eventsByDate.set(dateString, { ...event });
                                }
                            });

                            combinedEvents = Array.from(eventsByDate.values()).sort((a, b) => a.date - b.date);
                            
                        }
                    }
                    else if(index % 2 === 0){
                        found = longevityDateTable.find((element) => (element <= anniversaryOne) && longevityDateTable.indexOf(element) == lastFoundLongevityIndex);
                    }
                    else{
                        found = longevityDateTable.find((element) => (element <= anniversaryTwo) && longevityDateTable.indexOf(element) == lastFoundLongevityIndex);
                    }
                    // if the current anniversary date is greater than any date in longevityDateTable, grab the latest date and the index
                    let indexOf = longevityDateTable.indexOf(found);
                    let plusText = " + ";
                    if(props.jobTitle == "teacher" || props.jobTitle == "adult-education-teacher"){
                        plusText = " + L";
                    }
                    if(isWithinInterval(anniversaryOne, {start: longevityDateTable[lastFoundLongevityIndex],end: longevityDateTable[lastFoundLongevityIndex - 1]})){
                        stepWithLongevity = step + plusText + longevityTable[lastFoundLongevityIndex - 1];
                    }

                    if(indexOf == lastFoundLongevityIndex){
                        lastFoundLongevityIndex++;
                        stepWithLongevity = step + plusText + longevityTable[lastFoundLongevityIndex - 1];
                    }

                    if(stepProgression.length == index + 1 && lastFoundLongevityIndex < longevityDateTable.length){
                    for(i = lastFoundLongevityIndex; i < longevityDateTable.length; i++){
                            longevityRows.push(
                                <tr key={`longevity-${i}`}>
                                    <td>{longevityDateTable[i].toLocaleDateString("en-US", formateDate)}</td>
                                    <td><span className='salary-raise'>$</span></td>
                                    <td>{props.jobTitle === 'staff-nurse' || props.jobTitle === 'head-nurse' || props.jobTitle === 'supervisor-nurse' || props.jobTitle === 'occupational-therapist' || props.jobTitle === 'physical-therapist' || props.jobTitle === 'audiologist' ? 'Experience Differential' : 'Step'} {step}{plusText}{props.jobTitle === 'staff-nurse' || props.jobTitle === 'head-nurse' || props.jobTitle === 'supervisor-nurse' || props.jobTitle === 'occupational-therapist' || props.jobTitle === 'physical-therapist' || props.jobTitle === 'audiologist' ? longevityLengthText(longevityTable[i]) : longevityTable[i]}
                                    </td>
                                </tr>
                            );
                        }
                    }
                }
                
                return(
                    ((props.jobTitle !== 'staff-nurse' && props.jobTitle !== 'head-nurse' && props.jobTitle !== 'supervisor-nurse' && props.jobTitle !== 'occupational-therapist' && props.jobTitle !== 'physical-therapist' && props.jobTitle !== 'audiologist') || !props.includeLongevity) &&
                    <>
                        <tr key={index} data-index={index + 1}>
                            <td>{props.jobTitle === 'staff-nurse' || props.jobTitle === 'head-nurse' || props.jobTitle === 'supervisor-nurse' || props.jobTitle === 'occupational-therapist' || props.jobTitle === 'physical-therapist' || props.jobTitle === 'audiologist' ? (<span> {singleYearAnniversary.toLocaleDateString("en-US", formateDate)}</span>) : (index % 2 === 0 ? (<span> {anniversaryOne.toLocaleDateString("en-US", formateDate)}</span> ):(<span>{anniversaryTwo.toLocaleDateString("en-US", formateDate)}</span>))}</td>
                            <td>{hasSalaryIncrease && <span className='salary-raise'>$</span>}</td>
                            <td>
                                {stepWithLongevity === 'Senior Therapist' ? stepWithLongevity : `${props.jobTitle === 'staff-nurse' || props.jobTitle === 'head-nurse' || props.jobTitle === 'supervisor-nurse' || props.jobTitle === 'occupational-therapist' || props.jobTitle === 'physical-therapist' || props.jobTitle === 'audiologist' ? 'Experience Differential' : 'Step'} ${stepWithLongevity}`}
                            </td>
                        </tr>
                        {props.includeLongevity && longevityRows}
                    </>
                )   
            })}
            {(props.jobTitle === 'staff-nurse' || props.jobTitle === 'head-nurse' || props.jobTitle === 'supervisor-nurse' || props.jobTitle === 'occupational-therapist' || props.jobTitle === 'physical-therapist' || props.jobTitle === 'audiologist') &&
                combinedEvents.map((event, index) => (
                    <tr key={`combined-${index}`}>
                        <td>{event.date.toLocaleDateString("en-US", formateDate)}</td>
                        <td><span className='salary-raise'>$</span></td>
                        <td>{event.description}</td>
                    </tr>
                ))
            }
        </table>
        </div>
        <p className='content'><strong>$</strong> = Salary Increase</p>
    </div>
    </>
  );
}

export default SalarySchedule;